import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useMsal } from "@azure/msal-react";
import TenantApi from "../../api/tenant/tenant-api-factory";
import { PropertyDetailsTab } from '../../components/properties/details-accordian-tab';
import { PropertyRoomsTab } from '../../components/properties/rooms-accordian-tab';
import { PropertyGalleryTab } from '../../components/properties/gallery-accordian-tab';
import { PropertyInspectionsTab } from '../../components/properties/inspections-accordian-tab';
import { PropertyNotesTab } from '../../components/properties/notes.accordian-tab';
import { PropertyKeyCodesTab } from './keycodes-accordian-tab';
import { PropertyDocumentsTab } from './property-documents-tab';
import { PropertyMaintenanceTab } from './maintenance-tab';
import { PropertyTenantsTab } from './tenants-accordian-tab';
import { PropertyBrochureTab } from './brochure-accordian-tab';

export const PropertyTabsContent = ({
    propertyDocument,
    onPropertyChange, // optional: (updatedPropertyDocument) => void
    selectedProject,
    handleSaveProperty,
    selectedPropertyType,
    handleSetPropertyType,
    isHMO,
    selectedProjectName,
    photos,
    setShowUploadImageModal,
    setShowEditImageModal,
    setStarRating,
    propertyApi
}) => {

    // tenant loading lifted to this parent so it's fetched once and shared
    const { instance, inProgress, accounts } = useMsal();
    const tenantApi = useMemo(() => new TenantApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const [tenants, setTenants] = useState([]);
    const [loadingTenants, setLoadingTenants] = useState(false);
    const [fetchTenantsComplete, setFetchTenantsComplete] = useState(false);

    useEffect(() => {
        let mounted = true;
        const loadTenants = async () => {
            const landlordParentId = propertyDocument?.property?.landlordOrOwner?.parent?.id;
            if (!landlordParentId || fetchTenantsComplete) return;
            setLoadingTenants(true);
            try {
                const list = await tenantApi.list(
                    landlordParentId,
                    propertyDocument.property.landlordOrOwnerId,
                    propertyDocument.id
                );
                if (!mounted) return;
                setTenants(_.sortBy(list ?? [], 'displayName'));
                setFetchTenantsComplete(true);
            } catch (e) {
                console.error("Failed to load tenants", e);
                if (mounted) setTenants([]);
            } finally {
                if (mounted) setLoadingTenants(false);
            }
        };
        loadTenants();
        return () => { mounted = false; };
    }, [propertyDocument, tenantApi, fetchTenantsComplete]);

    const boardId = () => {
        return propertyDocument && propertyDocument.property && propertyDocument.property.selectedProject ? propertyDocument.property.selectedProject : null;
    };

    // Called by children when rooms change. Update propertyDocument.property.rooms and notify parent.
    const handleRoomsChange = (updatedRooms) => {
        const updatedDoc = {
            ...propertyDocument,
            property: {
                ...(propertyDocument?.property ?? {}),
                rooms: updatedRooms
            }
        };
        if (typeof onPropertyChange === "function") {
            onPropertyChange(updatedDoc);
            return;
        }
        // fallback: try to persist via handleSaveProperty safely
        if (typeof handleSaveProperty === "function") {
            try {
                handleSaveProperty(updatedDoc.property);
                return;
            } catch (err) {
                console.warn("handleSaveProperty(updatedDoc) threw, will try handleSaveProperty(updatedDoc.property).", err);
                try {
                    // some callers expect just the property object
                    handleSaveProperty(updatedDoc.property ?? updatedDoc);
                    return;
                } catch (err2) {
                    console.error("handleSaveProperty failed for both document and property shapes", err2, { updatedDoc });
                    return;
                }
            }
        }
        console.warn("rooms changed but no onPropertyChange or handleSaveProperty available", updatedDoc);
    };

    return (

        <div className="tab-content p-3 rounded-top panel rounded-0 m-0">

            <PropertyDetailsTab
                propertyDocument={propertyDocument}
                selectedProject={selectedProject}
                selectedPropertyType={selectedPropertyType}
                handleSetPropertyType={handleSetPropertyType}
                handleSaveProperty={handleSaveProperty}
            />

            <PropertyRoomsTab
                propertyDocument={propertyDocument}
                isHMO={isHMO}
                tenants={tenants}
                tenantsLoading={loadingTenants}
                onRoomsChange={handleRoomsChange}
            />

            <PropertyTenantsTab
                propertyDocument={propertyDocument}
                isHMO={isHMO}
                tenants={tenants}
                fetchComplete={fetchTenantsComplete}
                loading={loadingTenants}
            />

            <PropertyInspectionsTab
                propertyDocument={propertyDocument}
                projectName={selectedProjectName()}
                boardId={boardId()}
                saveProperty={handleSaveProperty}
            />

            <PropertyGalleryTab
                propertyDocument={propertyDocument}
                photos={photos}
                setShowUploadImageModal={setShowUploadImageModal}
                setShowEditImageModal={setShowEditImageModal}
                setStarRating={setStarRating}
            />

            <PropertyNotesTab
                propertyDocument={propertyDocument}
            />

            <PropertyMaintenanceTab
                propertyDocument={propertyDocument}
                saveProperty={handleSaveProperty}
            />

            <PropertyDocumentsTab
                propertyDocument={propertyDocument}
            />

            <PropertyKeyCodesTab
                propertyDocument={propertyDocument}
                propertyApi={propertyApi}
            />

            <PropertyBrochureTab
                propertyDocument={propertyDocument}
                photos={photos}
                setShowEditImageModal={setShowEditImageModal}
                setStarRating={setStarRating}
                onPropertyChange={onPropertyChange}
                handleSaveProperty={handleSaveProperty}
            />
        </div>
    );
}